import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightLeft, AlertTriangle, Download, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { justoAPI, blobErrorDetail } from '@/lib/justo';
import { downloadBlob, filenameFromHeaders, fmtNum, errorMsg } from '@/lib/recuentos';

export const LUGAR_GRECIA = 'grecia_principal';

const Aviso = ({ tone, title, items, testId, children }) => (
  <div className={`rounded-lg border p-3 text-sm ${tone}`} data-testid={testId}>
    <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {title} ({items.length})</p>
    <ul className="list-disc pl-5 mt-1 max-h-28 overflow-y-auto text-xs">{items.map((t) => <li key={t}>{t}</li>)}</ul>
    {children}
  </div>
);

const Filas = ({ filas }) => (
  <div className="max-h-56 overflow-auto border rounded-lg">
    <table className="w-full text-xs" data-testid="bodega-grecia-table">
      <thead className="bg-slate-50 text-slate-600 sticky top-0"><tr>
        <th className="px-2 py-1.5 text-left">Ingrediente</th><th className="px-2 py-1.5 text-right">Cantidad</th><th className="px-2 py-1.5 text-left">Unidad</th>
      </tr></thead>
      <tbody>{filas.map((f) => (
        <tr key={f.id_justo} className="border-t"><td className="px-2 py-1">{f.nombre}</td><td className="px-2 py-1 text-right font-semibold">{fmtNum(f.cantidad)}</td><td className="px-2 py-1">{f.unidad}</td></tr>
      ))}</tbody>
    </table>
  </div>
);

const Contenido = ({ p }) => (
  <div className="space-y-3">
    <p className="text-sm text-slate-600">Bodega Central → Bodega Principal (Grecia) · {p.filas.length} ingredientes</p>
    {p.faltantes.length > 0 && (
      <Aviso tone="bg-red-50 border-red-200 text-red-700" title="Productos sin equivalencia Justo" items={p.faltantes.map((f) => f.nombre)} testId="bodega-grecia-faltantes">
        <Link to="/admin/justo?tab=equivalencias" className="text-xs font-semibold underline">Completar equivalencias</Link>
      </Aviso>
    )}
    {p.no_existe.length > 0 && <Aviso tone="bg-red-50 border-red-200 text-red-700" title="No existen en la hoja Ingredientes de la plantilla" items={p.no_existe} testId="bodega-grecia-no-existe" />}
    {p.no_permitido.length > 0 && <Aviso tone="bg-red-50 border-red-200 text-red-700" title="No habilitados en Bodega Central y Bodega Principal" items={p.no_permitido} testId="bodega-grecia-no-permitido" />}
    {p.sin_stock.length > 0 && (
      <Aviso tone="bg-amber-50 border-amber-200 text-amber-800" title="Cantidad mayor al stock actual de Bodega Central (se puede descargar igual)"
        items={p.sin_stock.map((s) => `${s.nombre}: ${fmtNum(s.cantidad)} ${s.unidad} (stock ${fmtNum(s.stock)})`)} testId="bodega-grecia-sin-stock" />
    )}
    {p.filas.length > 0 && <Filas filas={p.filas} />}
  </div>
);

export const ExportBodegaGreciaButton = ({ tipo, id }) => {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(false);
  const abrir = async () => {
    setLoading(true);
    try { setP((await justoAPI.bodegaGreciaPreview(tipo, id)).data); }
    catch (e) { toast.error(errorMsg(e, 'No se pudo preparar la transferencia')); }
    finally { setLoading(false); }
  };
  const descargar = async () => {
    setLoading(true);
    try {
      const res = await justoAPI.bodegaGreciaExportar(tipo, id);
      downloadBlob(res.data, filenameFromHeaders(res.headers, p.filename));
      toast.success('Transferencia de bodega descargada');
      setP(null);
    } catch (e) { toast.error((await blobErrorDetail(e)) || 'No se pudo descargar'); }
    finally { setLoading(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={abrir} disabled={loading} className="border-violet-300 text-violet-700 hover:bg-violet-50" data-testid={`export-bodega-grecia-btn-${id}`}>
        {loading && !p ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ArrowRightLeft className="w-4 h-4 mr-1" />} Exportar transf. de bodega
      </Button>
      <Dialog open={!!p} onOpenChange={(o) => !o && setP(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="bodega-grecia-dialog">
          <DialogHeader><DialogTitle>Transferencia de bodega · N° {p?.numero}</DialogTitle></DialogHeader>
          {p && <Contenido p={p} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => setP(null)}>Cancelar</Button>
            <Button onClick={descargar} disabled={!p?.puede_descargar || loading} className="bg-violet-600 hover:bg-violet-700" data-testid="bodega-grecia-descargar-btn">
              <Download className="w-4 h-4 mr-1" /> Descargar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
